import type React from "react";
import type { ColorScheme } from "@/lib/colorSchemes";
import { CardDeck, type CardDeckProps } from "@/components/renderings/CardDeck";
import {
  CardCarousel,
  type CardCarouselProps,
} from "@/components/renderings/CardCarousel";
import {
  ContactForm,
  type ContactFormProps,
} from "@/components/renderings/ContactForm";
import {
  ContentColumns,
  type ContentColumnsProps,
} from "@/components/renderings/Content";
import { IconList, type IconListProps } from "@/components/renderings/IconList";
import {
  ImageSlot,
  type ImageSlotProps,
} from "@/components/renderings/ImageSlot";

/**
 * Discriminated union of every component that can fill a PageSection slot.
 * Add new entries here as new renderings are built.
 */
export type ChildSlot =
  | { type: "cardDeck"; props: CardDeckProps }
  | { type: "contactForm"; props: ContactFormProps }
  | { type: "cardCarousel"; props: CardCarouselProps }
  | { type: "contentColumns"; props: ContentColumnsProps }
  | { type: "iconList"; props: IconListProps }
  | { type: "image"; props: ImageSlotProps };

/** Used by the editor dropdown to list available slot types. */
export const CHILD_SLOT_TYPES = [
  { value: "cardDeck" as const, label: "Card Deck (Bento)" },
  { value: "contactForm" as const, label: "Contact Form" },
  { value: "cardCarousel" as const, label: "Card Carousel" },
  { value: "contentColumns" as const, label: "Content Columns" },
  { value: "iconList" as const, label: "Icon List" },
  { value: "image" as const, label: "Image" },
] satisfies { value: ChildSlot["type"]; label: string }[];

/** Render a ChildSlot entry — call this inside PageSection (server or client). */
export function renderChildSlot(
  slot: ChildSlot,
  parentColorScheme?: ColorScheme,
): React.ReactNode {
  switch (slot.type) {
    case "cardDeck":
      return (
        <CardDeck
          {...slot.props}
          colorScheme={slot.props.colorScheme ?? parentColorScheme}
        />
      );
    case "contactForm":
      return (
        <ContactForm
          {...slot.props}
          colorScheme={slot.props.colorScheme ?? parentColorScheme}
        />
      );
    case "cardCarousel":
      return (
        <CardCarousel
          {...slot.props}
          colorScheme={slot.props.colorScheme ?? parentColorScheme}
        />
      );
    case "contentColumns":
      return (
        <ContentColumns
          {...slot.props}
          colorScheme={slot.props.colorScheme ?? parentColorScheme}
        />
      );
    case "iconList":
      return (
        <IconList
          {...slot.props}
          colorScheme={slot.props.colorScheme ?? parentColorScheme}
        />
      );
    case "image":
      return <ImageSlot {...slot.props} />;
    default:
      return null;
  }
}
